/**
 * "Clone from Flow" (GitTab.jsx): Flow is the git server of every service, remote or not. Each service's clone URL
 * (`<cloneBase>/<service>.git`) with Copy, and an access token for this project (read, or read and write, 30 days):
 * shown once with Copy and the `git clone https://git:<token>@host/...` line to paste. A viewer gets read only.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Popover, Segmented } from 'antd';
import { Copy, DownloadSimple } from '@phosphor-icons/react';
import { createGitToken } from '../../services/GitService';
import { cloneCommand, flowCloneUrl } from './gitModel';
import { GitErrorText } from './GitDialogs';

export const TOKEN_DAYS = 30;

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function CopyButton({ text, label }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      aria-label={label}
      icon={<Copy aria-hidden size={14} />}
      onClick={async() => { if (await copyText(text)) { setDone(true); setTimeout(() => setDone(false), 1500); } }}
      size="small"
    >
      {done ? 'Copied' : 'Copy'}
    </Button>
  );
}

CopyButton.propTypes = { label: PropTypes.string.isRequired, text: PropTypes.string.isRequired };

export function CloneFromFlowPanel({ projectId, services, cloneBase, canWrite }) {
  const [mode, setMode] = useState('read');
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const urls = services.map((service) => ({ service, url: flowCloneUrl(cloneBase, service) })).filter((entry) => entry.url);
  const create = async() => {
    setBusy(true);
    setError(null);
    try {
      const answer = await createGitToken({ projectId, mode: canWrite ? mode : 'read', ttlDays: TOKEN_DAYS });
      setToken({ value: answer.token, expiresAt: answer.expiresAt || null, mode: canWrite ? mode : 'read' });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const first = urls[0];
  return (
    <div className="git-clone" data-testid="git-clone-panel">
      <p className="git-muted">Every service is a git repository on Flow: clone it on your computer. A push lands in the project.</p>
      {urls.length === 0 && <p className="git-muted">Flow does not offer cloning here yet.</p>}
      <ul className="git-clone-list">
        {urls.map((entry) => (
          <li data-testid={`git-clone-${entry.service}`} key={entry.service}>
            <span className="git-remote-name">{entry.service}</span>
            <code className="git-mono git-grow" title={entry.url}>{entry.url}</code>
            <CopyButton label={`Copy the clone URL of ${entry.service}`} text={entry.url} />
          </li>
        ))}
      </ul>
      {urls.length > 0 && !token && (
        <div className="git-clone-token">
          <strong>Access token</strong>
          <span className="git-muted">{`Your password for git on this project, valid ${TOKEN_DAYS} days.`}</span>
          <div className="git-row">
            {canWrite && (
              <Segmented
                aria-label="Token access"
                onChange={setMode}
                options={[{ value: 'read', label: 'Read' }, { value: 'write', label: 'Read and write' }]}
                size="small"
                value={mode}
              />
            )}
            <Button data-testid="git-token-create" loading={busy} onClick={create} size="small" type="primary">Create access token</Button>
          </div>
        </div>
      )}
      {token && (
        <div className="git-clone-token is-shown" data-testid="git-token-shown">
          <strong>{token.mode === 'write' ? 'Read and write token' : 'Read token'}</strong>
          <span className="git-banner is-warn">Copy it now: it is shown only once.</span>
          <div className="git-row">
            <code className="git-mono git-grow git-token" data-testid="git-token-value">{token.value}</code>
            <CopyButton label="Copy the token" text={token.value} />
          </div>
          {first && (
            <div className="git-row">
              <code className="git-mono git-grow" data-testid="git-token-command">{cloneCommand(first.url, token.value, first.service)}</code>
              <CopyButton label="Copy the clone command" text={cloneCommand(first.url, token.value, first.service)} />
            </div>
          )}
          {token.expiresAt && <span className="git-muted">{`Valid until ${new Date(token.expiresAt).toLocaleDateString()}.`}</span>}
        </div>
      )}
      <GitErrorText error={error} />
    </div>
  );
}

CloneFromFlowPanel.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  cloneBase: PropTypes.string,
  projectId: PropTypes.string.isRequired,
  services: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default function CloneFromFlow(props) {
  return (
    <Popover content={<CloneFromFlowPanel {...props} />} placement="bottomRight" rootClassName="git-clone-popover" title="Clone from Flow" trigger="click">
      <Button data-testid="git-clone-open" icon={<DownloadSimple aria-hidden size={16} />}>Clone from Flow</Button>
    </Popover>
  );
}
